import { onDocumentReady } from '@theme/utilities';
import { ThemeEvents, VariantUpdateEvent, CartUpdateEvent } from '@theme/events';

/**
 * Handles the load event for a lazy-loaded image, animating its transition
 * from the "lazy-loading" state to the "lazy-loaded" state.
 * @param {Event} event - The load event for the image.
 */
function handleOnLoad(event) {  
  const currentImage = event.currentTarget;
  
  // Prevent duplicate processing
  if (currentImage.classList.contains('lazy-loaded') || currentImage.classList.contains('lazy-loading')) {
    return;
  }
  
  // Add the "lazy-loading" class to start animation
  currentImage.classList.add('lazy-loading');
  // Remove the "lazy-loading" class and add "lazy-loaded" after animation
  setTimeout(() => {
    currentImage?.classList?.add('lazy-loaded');
    currentImage?.classList?.remove('lazy-loading');
  }, 250); // Match CSS animation duration
}

/**
 * Initializes the animation of a lazy-loaded image, either by marking it
 * as "lazy-loaded" if it has already been loaded, or by adding a "load" 
 * event listener to animate the image when it finishes loading.
 * @param {HTMLImageElement} image - The image element to animate.
 */
function handleImage(image) {
  // Prevent duplicate processing
  if (image.classList.contains('lazy-loaded') || image.classList.contains('lazy-loading')) {
    return;
  }
  
  if (image.complete) {
    // If the image has already loaded, mark it as "lazy-loaded" immediately.
    image.classList.add('lazy-loading');
    setTimeout(() => {
      image.classList.add('lazy-loaded');
      image.classList.remove('lazy-loading');
    }, 250);
    image.removeEventListener('load', handleOnLoad, false);
  } else {
    // Otherwise, add a "load" event listener to animate the image when it loads.
    image.addEventListener('load', handleOnLoad, false);
  }
}

/**
 * Initializes the animation of all lazy-loaded images that are not already 
 * marked as "lazy-loaded" within the specified root element and its subtree.
 * @param {HTMLElement} rootEl - The root element to search for images.
 */
function initializeLazyImageAnimation(rootEl = document) {
  // Find all lazy-loaded images within the root element and its subtree,
  // and initialize their animation.
  const images = rootEl.querySelectorAll('img[loading^="lazy"]:not(.lazy-loaded)');
  
  images.forEach((image) => {
    // Use a more direct approach - check if image is loaded and animate immediately
    if (image.complete && image.naturalHeight !== 0) {
      // Image is already loaded, animate immediately
      image.classList.add('lazy-loading');
      requestAnimationFrame(() => {
        setTimeout(() => {
          image.classList.add('lazy-loaded');
          image.classList.remove('lazy-loading');
        }, 250);
      });
    } else {
      // Image is not loaded yet, wait for it
      const handleLoad = () => {
        image.classList.add('lazy-loading');
        requestAnimationFrame(() => {
          setTimeout(() => {
            image.classList.add('lazy-loaded');
            image.classList.remove('lazy-loading');
          }, 250);
        });
        image.removeEventListener('load', handleLoad);
      };
      image.addEventListener('load', handleLoad);
    }
  });
}

/**
 * The mutation observer instance for lazy loading
 * @type {MutationObserver | null}
 */
let lazyLoadingObserver = null;

/**
 * Initializes the lazy loading system using MutationObserver
 * Applies classes to images with 'loading="lazy"' when they load
 * @param {Element} [rootEl] - The root element to search within (defaults to document)
 * @param {boolean} [isDesignModeEvent] - Whether this is being called from theme editor
 */
function initializeLazyLoading(rootEl = document, isDesignModeEvent = false) {
  // Use the simplified animation function
  initializeLazyImageAnimation(rootEl);

  // Create mutation observer if it doesn't exist
  if (!lazyLoadingObserver) {
    lazyLoadingObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            // If a new image with the "lazy" attribute is added, initialize its animation.
            if (node.tagName === 'IMG' && node.getAttribute('loading') === 'lazy') {
              initializeLazyImageAnimation(node);
            }
            // If a new node is added that has children, recursively initialize its children.
            else if (node.firstChild !== null) {
              initializeLazyImageAnimation(node);
            }
          });
        }
      });
    });
  }

  // Observe the root element and its subtree for changes
  const config = { childList: true, subtree: true };
  lazyLoadingObserver.observe(rootEl, config);
}

/**
 * Disconnects the lazy loading observer
 */
function disconnectLazyLoadingObserver() {
  if (lazyLoadingObserver) {
    lazyLoadingObserver.disconnect();
    lazyLoadingObserver = null;
  }
}

// Initialize on DOM ready
onDocumentReady(() => {
  // Add .js class to enable CSS animations
  document.documentElement.classList.add('js');
  initializeLazyLoading();
  
  // Also run after a short delay to catch any late-loading images
  setTimeout(() => {
    initializeLazyLoading();
  }, 100);
  
  // And again after a longer delay for any really slow images
  setTimeout(() => {
    initializeLazyLoading();
  }, 1000);
});

// Handle theme editor section loading
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeLazyLoading(event.target, true);
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    initializeLazyLoading(document, true);
  });
}

// Handle dynamic content updates from variant changes and cart updates
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (event.detail.data.html) {
    // Initialize lazy loading for new content in the variant update
    initializeLazyLoading(event.detail.data.html, false);
  }
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail.data.sections) {
    // Initialize lazy loading for updated sections
    Object.values(event.detail.data.sections).forEach((sectionHtml) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHtml;
      initializeLazyLoading(tempDiv, false);
    });
  }
});

// Export functions for manual use
export { initializeLazyLoading, disconnectLazyLoadingObserver };
